'use client';

import { PortableTextSimple } from '@/components/portable-text/portable-text-simple';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { portableTextModuleStyles } from '@/lib/utils';
import {
  WastageSwitch,
  useHasWastageState,
} from '@veranda/shared/components/pages/product-details';
import type { ProductPageProps } from '@veranda/shared/components/pages/product-details/client';
import { cn } from '@veranda/shared/lib';
import { SanityImage } from '@veranda/shared/sanity/utils/components';
import { Dialog as DialogPrimitive, Switch } from 'radix-ui';
import { useState } from 'react';

export function ProductWastageSwitch({
  product,
}: {
  product: ProductPageProps;
}) {
  const { hasWastage, setHasWastage } = useHasWastageState();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleWastageToggle = (checked: boolean) => {
    if (checked) {
      setHasWastage(true);
    } else {
      setDialogOpen(true);
    }
  };

  return (
    <>
      <WastageSwitch>
        <WastageSwitch.Label className="font-bold text-brand-burgundy text-sm">
          <span className="max-sm:hidden">Please</span> add 15% wastage
        </WastageSwitch.Label>
        <Switch.Root
          className="relative h-7 w-12 cursor-pointer rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-bg focus:ring-offset-2 data-[state=checked]:bg-brand-burgundy"
          checked={hasWastage}
          onCheckedChange={handleWastageToggle}
        >
          <Switch.Thumb className="block size-6 translate-x-0.5 rounded-full bg-white shadow-lg transition-transform duration-100 will-change-transform data-[state=checked]:translate-x-[22px]" />
        </Switch.Root>
      </WastageSwitch>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="grid sm:max-w-2xl lg:grid-cols-2">
          {product.wastageDialog?.image && (
            <div className="relative isolate">
              <div className="aspect-h-1 aspect-w-1">
                <SanityImage image={product.wastageDialog.image} fill />
              </div>
            </div>
          )}
          <DialogHeader>
            <DialogTitle className="typography-clash-h3 mb-4 text-primary tracking-wide">
              {product.wastageDialog?.title}
            </DialogTitle>
            <div className="grid h-full">
              <DialogDescription>
                {product.wastageDialog?.content && (
                  <div
                    className={cn(
                      'noMarginFirstChild gap-4',
                      portableTextModuleStyles
                    )}
                  >
                    <PortableTextSimple
                      content={product.wastageDialog.content}
                    />
                  </div>
                )}
              </DialogDescription>
              <div className="mt-auto grid grid-cols-2 gap-4">
                <DialogPrimitive.Close asChild>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      setHasWastage(true);
                      setDialogOpen(false);
                    }}
                  >
                    Keep Wastage
                  </Button>
                </DialogPrimitive.Close>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setHasWastage(false);
                    setDialogOpen(false);
                  }}
                >
                  No Wastage
                </Button>
              </div>
            </div>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}
